import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import {
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  Input,
  InputGroup,
  InputGroupAddon,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

import config from '../../../config';
import { useDateFilter } from '../../../contexts/DateFilterContext';
import { useCity } from '../../../contexts/CityContext';
import s from './TripList.module.scss';
import { buildGoogleMapsRouteUrl, getMapsRouteLabel } from '../../../utils/googleMapsRoute';

const getNamedStops = (stops) =>
  (Array.isArray(stops) ? stops : [])
    .map((stop) => (typeof stop === 'string' ? stop : (stop?.location_name || stop?.address || '')))
    .map((value) => String(value).trim())
    .filter((value) => value && value !== 'N/A');

const getSingleLocation = (value) => {
  const text = String(value || '').trim();
  return text && text !== 'N/A' ? [text] : [];
};

const getTripRouteStops = (trip) => {
  const pickups = getNamedStops(trip?.pickup_stops);
  const drops = getNamedStops(trip?.drop_stops);
  const additionalDrops = getNamedStops(
    (trip?.additional_drops || []).map((drop) => drop?.drop_location)
  );
  return [
    ...(pickups.length ? pickups : getSingleLocation(trip?.pickup_location)),
    ...(drops.length ? drops : getSingleLocation(trip?.drop_location)),
    ...additionalDrops,
  ];
};

const getPartRouteStops = (item) =>
  (item?.steps || [])
    .map((step) => String(step?.location_name || '').trim())
    .filter((value) => value && value !== 'N/A');

// Reminder state lives on the server (Redis) so every ops user shares the same
// cutoff: only trips completed after it was switched on are flagged.

// Configure axios
axios.defaults.baseURL = config.apiUrl;
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

class TripList extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      searchTerm: '',
      activeTab: this.props.location?.pathname?.includes('/part-routes')
        ? 'routes'
        : this.props.location?.pathname?.includes('/part-trips')
          ? 'part'
          : this.props.location?.pathname?.includes('/cluster')
            ? 'cluster'
            : 'live',
      trips: [],
      clusterBookings: [],
      clusterUpdatedAt: null,
      totalClusterBookings: 0,
      clusterDates: [],
      clusterSelectedDate: null,
      clusterToday: null,
      clusterIsPreview: false,
      partTrips: [],
      partRoutes: [],
      partRouteSnapshots: [],
      clearingRouteVehicleId: null,
      completingRouteVehicleId: null,
      completingAndSendingRouteVehicleId: null,
      isFetching: false,
      isPageFetching: false,
      isBackgroundRefreshing: false,
      hasLoadedLive: false,
      hasLoadedPart: false,
      hasLoadedRoutes: false,
      hasLoadedCluster: false,
      error: null,
      // Pagination state
      currentPage: 1,
      pageInput: 1,
      itemsPerPage: 20,
      totalTrips: 0,
      totalPartTrips: 0,
      totalPartRoutes: 0,
      // Auto-refresh countdown
      refreshIntervalSeconds: 10,
      nextRefreshIn: 10,
      // Auto-assignment pause state
      autoAssignPaused: false,
      togglingPause: false,
      clusterAssigningKey: null,
      expandedBookings: [],
      // Completed trips blink until backend pay flags are both true
      labourPayModalOpen: false,
      labourPayTrip: null,
      tripPayAmount: '',
      labourPayAmount: '',
      isUpdatingLabourPay: false,
      markPaidModalOpen: false,
      markPaidTrip: null,
      isMarkingPaid: false,
      transporterVehicleModal: {
        isOpen: false,
        trip: null,
        activeTab: 'assign',
        vehicles: [],
        isLoadingVehicles: false,
        assigningVehicleId: null,
        error: null,
      },
      sendingLocationKey: null,
    };
    
    // Request tracking to prevent out-of-order responses during fast pagination
    this._tripsReqId = 0;
    this._partTripsReqId = 0;
    this._partRoutesReqId = 0;
    this._clusterReqId = 0;

    // Countdown timer for auto-refresh
    this.countdownTimer = null;
    this.refreshInterval = null;
    // Scroll position saved before background refreshes
    this._savedScrollY = 0;
  }

  componentDidMount() {
    if (this.state.activeTab === 'part') {
      this.fetchPartTrips();
    } else if (this.state.activeTab === 'routes') {
      this.fetchPartRoutes();
    } else if (this.state.activeTab === 'cluster') {
      this.fetchClusterDates().then(() => this.fetchClusterSuitable());
    } else {
      this.fetchTrips();
    }
    // Fetch scheduler pause state on mount
    this.fetchSchedulerStatus();
    // Set up auto-refresh every 10 seconds (background = silent, no spinner)
    this.refreshInterval = setInterval(() => {
      // Skip auto-refresh while a manual assignment is in progress to avoid
      // showing an intermediate snapshot mid-batch.
      if (this.state.clusterAssigningKey != null) return;

      if (this.state.activeTab === 'part') {
        this.fetchPartTrips({ background: true });
      } else if (this.state.activeTab === 'routes') {
        this.fetchPartRoutes({ background: true });
      } else if (this.state.activeTab === 'cluster') {
        this.fetchClusterSuitable({ background: true });
      } else {
        this.fetchTrips({ background: true });
      }
    }, 10000);
    // Start countdown timer
    this.countdownTimer = setInterval(() => {
      this.setState(prev => ({
        nextRefreshIn: prev.nextRefreshIn > 0 ? prev.nextRefreshIn - 1 : 0
      }));
    }, 1000);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location?.pathname !== this.props.location?.pathname) {
      const nextTab = this.props.location?.pathname?.includes('/part-routes')
        ? 'routes'
        : this.props.location?.pathname?.includes('/part-trips')
          ? 'part'
          : this.props.location?.pathname?.includes('/cluster')
            ? 'cluster'
            : 'live';
      if (nextTab !== this.state.activeTab) {
        this.setState({ activeTab: nextTab, currentPage: 1, pageInput: 1 }, () => {
          if (nextTab === 'part') {
            this.fetchPartTrips();
          } else if (nextTab === 'routes') {
            this.fetchPartRoutes();
          } else if (nextTab === 'cluster') {
            this.fetchClusterDates().then(() => this.fetchClusterSuitable());
          } else {
            this.fetchTrips();
          }
        });
      }
    }

    // Refetch trips when selected cities change
    const citiesChanged = JSON.stringify(prevProps.selectedCities) !== JSON.stringify(this.props.selectedCities);
    // Refetch trips when date range changes
    const dateChanged =
      prevProps.dateRange?.startDate !== this.props.dateRange?.startDate ||
      prevProps.dateRange?.endDate   !== this.props.dateRange?.endDate;

    if (citiesChanged || dateChanged) {
      this.setState({ currentPage: 1, pageInput: 1 }, () => {
        if (this.state.activeTab === 'part') {
          this.fetchPartTrips();
        } else if (this.state.activeTab === 'routes') {
          this.fetchPartRoutes();
        } else if (this.state.activeTab === 'cluster') {
          this.fetchClusterSuitable();
        } else {
          this.fetchTrips();
        }
      });
    }
  }

  componentWillUnmount() {
    if (this._searchDebounceTimer) {
      clearTimeout(this._searchDebounceTimer);
    }
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }

  fetchSchedulerStatus = async () => {
    try {
      const res = await axios.get(`${config.dispatchApiBase}/scheduler/status`);
      this.setState({ autoAssignPaused: res.data.paused });
    } catch (e) {
      // Silent — don't disrupt page if status check fails
    }
  }

  toggleAutoAssign = async () => {
    const { autoAssignPaused } = this.state;
    this.setState({ togglingPause: true });
    try {
      const action = autoAssignPaused ? 'resume' : 'pause';
      await axios.post(`${config.dispatchApiBase}/scheduler/${action}`);
      await this.fetchSchedulerStatus();
      toast.success(autoAssignPaused ? '▶ Auto-assignment resumed' : '⏸ Auto-assignment paused');
    } catch (e) {
      toast.error('Failed to toggle auto-assignment');
    } finally {
      this.setState({ togglingPause: false });
    }
  }

  fetchTrips = async ({ background = false, page = null } = {}) => {
    const isPageChange = page !== null;
    const targetPage = isPageChange ? page : this.state.currentPage;

    if (background) {
      this._savedScrollY = window.scrollY;
      this.setState({ isBackgroundRefreshing: true });
    } else if (isPageChange) {
      this.setState({ isPageFetching: true, error: null });
    } else {
      this.setState({ isFetching: true, error: null });
    }

    const currentReqId = ++this._tripsReqId;

    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add cities filter if any cities are selected
      if (this.props.selectedCities && this.props.selectedCities.length > 0) {
        params.append('cities', this.props.selectedCities.join(','));
      }

      // Add date range filter — let the server do the filtering so pagination is accurate
      const { startDate, endDate } = this.props.dateRange || {};
      if (startDate) params.append('from_date', startDate);
      if (endDate)   params.append('to_date',   endDate);
      
      // Add search term to filter trips across all pages
      if (this.state.searchTerm && this.state.searchTerm.trim()) {
        params.append('search', this.state.searchTerm.trim());
      }

      // Add pagination parameters
      const { itemsPerPage } = this.state;
      params.append('limit', itemsPerPage.toString());
      params.append('offset', ((targetPage - 1) * itemsPerPage).toString());
      
      const queryString = params.toString();
      const url = `/trips${queryString ? `?${queryString}` : ''}`;
      
      const response = await axios.get(url);

      if (currentReqId !== this._tripsReqId) return;

      this.setState({ 
        trips: response.data.trips || [],
        totalTrips: response.data.total || 0,
        hasLoadedLive: true,
        isFetching: false,
        isPageFetching: false,
        isBackgroundRefreshing: false,
        nextRefreshIn: this.state.refreshIntervalSeconds,
      }, () => {
        if (background) {
          window.scrollTo({ top: this._savedScrollY, behavior: 'instant' });
        }
      });
    } catch (error) {
      if (currentReqId !== this._tripsReqId) return;
      console.error('Failed to fetch trips:', error);
      this.setState({ 
        error: background ? null : (error.response?.data?.detail || 'Failed to fetch trips'),
        isFetching: false,
        isPageFetching: false,
        isBackgroundRefreshing: false,
      });
      if (!background) toast.error('Failed to fetch trips');
    }
  }

  fetchPartTrips = async ({ background = false, page = null } = {}) => {
    const isPageChange = page !== null;
    const targetPage = isPageChange ? page : this.state.currentPage;

    if (background) {
      this._savedScrollY = window.scrollY;
      this.setState({ isBackgroundRefreshing: true });
    } else if (isPageChange) {
      this.setState({ isPageFetching: true, error: null });
    } else {
      this.setState({ isFetching: true, error: null });
    }

    const currentReqId = ++this._partTripsReqId;

    try {
      const params = new URLSearchParams();
      const { startDate, endDate } = this.props.dateRange || {};
      if (startDate) params.append('from_date', startDate);
      if (endDate) params.append('to_date', endDate);
      if (this.state.searchTerm && this.state.searchTerm.trim()) {
        params.append('search', this.state.searchTerm.trim());
      }
      const { itemsPerPage } = this.state;
      params.append('limit', itemsPerPage.toString());
      params.append('offset', ((targetPage - 1) * itemsPerPage).toString());

      const queryString = params.toString();
      const url = `/part-trips${queryString ? `?${queryString}` : ''}`;
      const response = await axios.get(url);

      if (currentReqId !== this._partTripsReqId) return;

      this.setState({
        partTrips: response.data.part_trips || [],
        totalPartTrips: response.data.total || 0,
        hasLoadedPart: true,
        isFetching: false,
        isPageFetching: false,
        isBackgroundRefreshing: false,
        nextRefreshIn: this.state.refreshIntervalSeconds,
      }, () => {
        if (background) {
          window.scrollTo({ top: this._savedScrollY, behavior: 'instant' });
        }
      });
    } catch (error) {
      if (currentReqId !== this._partTripsReqId) return;
      console.error('Failed to fetch part trips:', error);
      this.setState({
        error: background ? null : (error.response?.data?.detail || 'Failed to fetch part trips'),
        isFetching: false,
        isPageFetching: false,
        isBackgroundRefreshing: false,
      });
      if (!background) toast.error('Failed to fetch part trips');
    }
  }

  fetchClusterDates = async () => {
    try {
      const res = await axios.get(`${config.dispatchApiBase}/cluster-dates`);
      if (!res.data?.success) return;
      const dates = res.data.dates || [];
      const today = res.data.today || dates[0] || null;
      this.setState((prev) => ({
        clusterDates: dates,
        clusterToday: today,
        clusterSelectedDate: prev.clusterSelectedDate || today,
      }));
    } catch (err) {
      console.warn('Failed to fetch cluster dates:', err);
    }
  };

  handleClusterDateChange = (e) => {
    const value = e.target.value;
    this.setState({ clusterSelectedDate: value, currentPage: 1, pageInput: 1 }, () => {
      this.fetchClusterSuitable();
    });
  };

  fetchClusterSuitable = async ({ background = false } = {}) => {
    if (background) {
      this._savedScrollY = window.scrollY;
      this.setState({ isBackgroundRefreshing: true });
    } else {
      this.setState({ isFetching: true, error: null });
    }

    const currentReqId = ++this._clusterReqId;

    try {
      const { clusterSelectedDate, clusterToday } = this.state;
      const params = { limit: 5000, offset: 0 };
      if (clusterSelectedDate && clusterToday && clusterSelectedDate !== clusterToday) {
        params.date = clusterSelectedDate;
      }
      const res = await axios.get(`${config.dispatchApiBase}/cluster-suitable`, {
        params,
      });
      if (currentReqId !== this._clusterReqId) return;
      if (!res.data?.success) {
        throw new Error(res.data?.detail || 'Cluster snapshot unavailable');
      }
      this.setState({
        clusterBookings: res.data.data || [],
        clusterUpdatedAt: res.data.updated_at || null,
        totalClusterBookings: res.data.total ?? (res.data.data || []).length,
        clusterIsPreview: Boolean(res.data.is_preview),
        hasLoadedCluster: true,
        isFetching: false,
        isPageFetching: false,
        isBackgroundRefreshing: false,
        nextRefreshIn: this.state.refreshIntervalSeconds,
      }, () => {
        if (background) {
          window.scrollTo({ top: this._savedScrollY, behavior: 'instant' });
        }
      });
    } catch (error) {
      if (currentReqId !== this._clusterReqId) return;
      console.error('Failed to fetch cluster suitable:', error);
      this.setState({
        error: background ? null : (error.response?.data?.detail || error.message || 'Failed to load cluster data'),
        isFetching: false,
        isPageFetching: false,
        isBackgroundRefreshing: false,
      });
      if (!background) toast.error('Failed to load cluster (suitable vehicles) data');
    }
  }

  assignClusterVehicle = async (bookingId, vehicleId) => {
    const bid = Number(bookingId);
    const vid = Number(vehicleId);
    if (!Number.isInteger(bid) || bid <= 0 || !Number.isInteger(vid) || vid <= 0) {
      toast.error('Invalid booking or vehicle id');
      return;
    }
    const key = `${bid}-${vid}`;
    const ok = window.confirm(
      `Assign booking #${bid} to vehicle #${vid}? This uses the same manual assignment as Bookings (queue + assignment state + driver gRPC).`
    );
    if (!ok) return;

    this.setState({ clusterAssigningKey: key });
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const operatorName =
        localStorage.getItem('user_name') ||
        localStorage.getItem('username') ||
        'Unknown Operator';

      const response = await fetch(`${config.dispatchApiBase}/manual-assignment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          booking_id: bid,
          vehicle_id: vid,
          assigned_by: operatorName,
          source: 'cluster_tab',
          confirmed: true,
          warnings_acknowledged: true,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.detail || data.message || `HTTP ${response.status}`);
      }
      toast.success(data.message || `Queued booking #${bid} to vehicle #${vid}`);
      await this.fetchClusterSuitable();
    } catch (err) {
      console.error('Cluster assign error:', err);
      toast.error(err.message || 'Assignment failed');
    } finally {
      this.setState({ clusterAssigningKey: null });
    }
  };

  assignClusterVehicleBatch = async (bookingIds, vehicleId) => {
    const ids = [...new Set(bookingIds.map((id) => Number(id)).filter((id) => id > 0))];
    const vid = Number(vehicleId);
    if (!ids.length || !Number.isInteger(vid) || vid <= 0) {
      toast.error('Invalid booking or vehicle id');
      return;
    }
    const ok = window.confirm(
      `Assign bookings ${ids.join(', ')} to vehicle #${vid}? Uses the same manual assignment as Bookings.`
    );
    if (!ok) return;

    const key = `cluster-${ids.join('-')}-${vid}`;
    this.setState({ clusterAssigningKey: key });
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const operatorName =
        localStorage.getItem('user_name') ||
        localStorage.getItem('username') ||
        'Unknown Operator';
      let failed = 0;
      for (const bid of ids) {
        const response = await fetch(`${config.dispatchApiBase}/manual-assignment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            booking_id: bid,
            vehicle_id: vid,
            assigned_by: operatorName,
            source: 'cluster_tab',
            confirmed: true,
            warnings_acknowledged: true,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false) {
          failed += 1;
          console.error(`Cluster batch assign failed for ${bid}:`, data);
        }
      }
      if (failed > 0) {
        toast.warning(`Assigned ${ids.length - failed}/${ids.length} bookings to vehicle #${vid}`);
      } else {
        toast.success(`Queued ${ids.length} booking(s) to vehicle #${vid}`);
      }
      await this.fetchClusterSuitable();
    } catch (err) {
      console.error('Cluster batch assign error:', err);
      toast.error(err.message || 'Batch assignment failed');
    } finally {
      this.setState({ clusterAssigningKey: null });
    }
  };

  fetchPartRoutes = async ({ background = false, page = null } = {}) => {
    const isPageChange = page !== null;
    const targetPage = isPageChange ? page : this.state.currentPage;

    if (background) {
      this._savedScrollY = window.scrollY;
      this.setState({ isBackgroundRefreshing: true });
    } else if (isPageChange) {
      this.setState({ isPageFetching: true, error: null });
    } else {
      this.setState({ isFetching: true, error: null });
    }

    const currentReqId = ++this._partRoutesReqId;

    try {
      const params = new URLSearchParams();
      const { itemsPerPage } = this.state;
      params.append('limit', itemsPerPage.toString());
      params.append('offset', ((targetPage - 1) * itemsPerPage).toString());

      const queryString = params.toString();
      const url = `/part-bookings/routes${queryString ? `?${queryString}` : ''}`;
      const response = await axios.get(url);

      const snapshotParams = new URLSearchParams();
      const { startDate, endDate } = this.props.dateRange || {};
      if (startDate) snapshotParams.append('from_date', startDate);
      if (endDate) snapshotParams.append('to_date', endDate);
      if (this.state.searchTerm) snapshotParams.append('search', this.state.searchTerm);
      snapshotParams.append('limit', '500');
      snapshotParams.append('offset', '0');
      const snapshotUrl = `/part-bookings/route-snapshots${snapshotParams.toString() ? `?${snapshotParams.toString()}` : ''}`;
      const snapshotResponse = await axios.get(snapshotUrl);

      if (currentReqId !== this._partRoutesReqId) return;

      this.setState({
        partRoutes: response.data.part_routes || [],
        partRouteSnapshots: snapshotResponse.data.snapshots || [],
        totalPartRoutes: response.data.total || 0,
        hasLoadedRoutes: true,
        isFetching: false,
        isPageFetching: false,
        isBackgroundRefreshing: false,
        nextRefreshIn: this.state.refreshIntervalSeconds,
      }, () => {
        if (background) {
          window.scrollTo({ top: this._savedScrollY, behavior: 'instant' });
        }
      });
    } catch (error) {
      if (currentReqId !== this._partRoutesReqId) return;
      console.error('Failed to fetch part routes:', error);
      this.setState({
        error: background ? null : (error.response?.data?.detail || 'Failed to fetch part routes'),
        isFetching: false,
        isPageFetching: false,
        isBackgroundRefreshing: false,
      });
      if (!background) toast.error('Failed to fetch part routes');
    }
  }

  clearPartRoute = async (vehicleId) => {
    if (!vehicleId) return;
    const confirmed = window.confirm(`Clear Redis route for vehicle #${vehicleId}?`);
    if (!confirmed) return;

    this.setState({ clearingRouteVehicleId: vehicleId });
    try {
      const response = await axios.delete(`/part-bookings/routes/${vehicleId}`);
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Failed to clear route');
      }
      toast.success(response?.data?.message || `Route cleared for vehicle #${vehicleId}`);
      await this.fetchPartRoutes();
    } catch (error) {
      console.error('Failed to clear part route:', error);
      const message = error.response?.data?.detail || error.message || 'Failed to clear route';
      toast.error(message);
    } finally {
      this.setState({ clearingRouteVehicleId: null });
    }
  }

  completePartRouteStep = async (routeItem, sendDriverUpdate = false) => {
    const vehicleId = routeItem?.vehicle_id;
    const stepIndex = routeItem?.current_step_index;
    if (!vehicleId) return;
    if (!stepIndex) {
      toast.info('No pending step to complete for this route');
      return;
    }

    const currentStep = (routeItem?.steps || []).find((step) => Number(step.step) === Number(stepIndex));
    const stepLabel = currentStep
      ? `#${stepIndex} ${String(currentStep.action || '').toUpperCase()} B${currentStep.booking_id}`
      : `#${stepIndex}`;

    const confirmText = sendDriverUpdate
      ? `Mark current step as completed and send next driver message for vehicle #${vehicleId}?\n${stepLabel}`
      : `Mark current step as completed for vehicle #${vehicleId}?\n${stepLabel}`;
    const confirmed = window.confirm(confirmText);
    if (!confirmed) return;

    this.setState(
      sendDriverUpdate
        ? { completingAndSendingRouteVehicleId: vehicleId }
        : { completingRouteVehicleId: vehicleId }
    );
    try {
      const response = await axios.post('/part-bookings/ops-complete-step', {
        vehicle_id: vehicleId,
        step_index: stepIndex,
        send_driver_update: Boolean(sendDriverUpdate),
      });

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Failed to complete step');
      }

      toast.success(
        response?.data?.message
          || (sendDriverUpdate
            ? `Completed step ${stepIndex} and sent next message for vehicle #${vehicleId}`
            : `Completed step ${stepIndex} for vehicle #${vehicleId}`)
      );
      await this.fetchPartRoutes();
      await this.fetchPartTrips();
    } catch (error) {
      console.error('Failed to complete part route step:', error);
      const message = error.response?.data?.detail || error.message || 'Failed to complete step';
      toast.error(message);
    } finally {
      this.setState({
        completingRouteVehicleId: null,
        completingAndSendingRouteVehicleId: null,
      });
    }
  }

  handleTabChange = (tab) => {
    if (tab === this.state.activeTab) return;
    const nextPath = tab === 'part'
      ? '/app/trips/part-trips'
      : tab === 'routes'
        ? '/app/trips/part-routes'
        : tab === 'cluster'
          ? '/app/trips/cluster'
          : '/app/trips';
    this.props.history.push(nextPath);
  }

  handleSearch = (e) => {
    const value = e.target.value;
    this.setState({ searchTerm: value, currentPage: 1, pageInput: 1 });
    if (this._searchDebounceTimer) {
      clearTimeout(this._searchDebounceTimer);
    }
    this._searchDebounceTimer = setTimeout(() => {
      this.triggerActiveTabFetch({ page: 1 });
    }, 350);
  }

  handleSearchSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (this._searchDebounceTimer) {
      clearTimeout(this._searchDebounceTimer);
    }
    this.setState({ currentPage: 1, pageInput: 1 }, () => {
      this.triggerActiveTabFetch({ page: 1 });
    });
  }

  handleClearSearch = () => {
    if (this._searchDebounceTimer) {
      clearTimeout(this._searchDebounceTimer);
    }
    this.setState({ searchTerm: '', currentPage: 1, pageInput: 1 }, () => {
      this.triggerActiveTabFetch({ page: 1 });
    });
  }

  triggerActiveTabFetch = (options = {}) => {
    const { activeTab } = this.state;
    if (activeTab === 'part') {
      this.fetchPartTrips(options);
    } else if (activeTab === 'routes') {
      this.fetchPartRoutes(options);
    } else if (activeTab === 'cluster') {
      this.fetchClusterSuitable(options);
    } else {
      this.fetchTrips(options);
    }
  }

  handlePageChange = (page) => {
    const totalPages = this.getTotalPages();
    if (page < 1 || page > totalPages || page === this.state.currentPage) {
      return;
    }
    this.setState({ currentPage: page, pageInput: page, isPageFetching: true }, () => {
      if (this.state.activeTab === 'part') {
        this.fetchPartTrips({ page });
      } else if (this.state.activeTab === 'routes') {
        this.fetchPartRoutes({ page });
      } else if (this.state.activeTab === 'cluster') {
        this.setState({ isPageFetching: false });
      } else {
        this.fetchTrips({ page });
      }
    });
  }

  getTotalPages = () => {
    const {
      activeTab,
      itemsPerPage,
      totalTrips,
      totalPartTrips,
      totalPartRoutes,
      clusterBookings,
    } = this.state;
    const activeTotal = activeTab === 'part'
      ? totalPartTrips
      : activeTab === 'routes'
        ? totalPartRoutes
        : activeTab === 'cluster'
          ? this.filterClusterBookings(clusterBookings).length
          : totalTrips;
    return Math.ceil(activeTotal / itemsPerPage) || 1;
  }

  handlePageInputChange = (e) => {
    const value = e.target.value;
    this.setState({ pageInput: value });
    const num = parseInt(value, 10);
    const totalPages = this.getTotalPages();
    if (Number.isFinite(num) && num >= 1 && num <= totalPages && num !== this.state.currentPage) {
      this.handlePageChange(num);
    }
  }

  handlePageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const num = parseInt(this.state.pageInput, 10);
      const totalPages = this.getTotalPages();
      if (Number.isFinite(num) && num >= 1 && num <= totalPages) {
        if (num !== this.state.currentPage) {
          this.handlePageChange(num);
        }
      } else {
        this.setState({ pageInput: this.state.currentPage });
      }
    }
  }

  handlePageInputBlur = () => {
    this.setState({ pageInput: this.state.currentPage });
  }

  filterTrips = (trips) => {
    // Search is handled server-side across all database records
    return trips;
  }

  filterPartTrips = (partTrips) => {
    // Search is handled server-side across all database records
    return partTrips;
  }

  filterPartRoutes = (partRoutes) => {
    return partRoutes.filter((item) => {
      if (!this.state.searchTerm) return true;
      const q = this.state.searchTerm.toLowerCase();
      const stepText = (item.steps || [])
        .map((step) => `${step.action || ''} ${step.booking_id || ''} ${step.location_name || ''}`)
        .join(' ')
        .toLowerCase();

      return (
        String(item.vehicle_id || '').includes(q)
        || (item.vehicle_registration_no || '').toLowerCase().includes(q)
        || (item.driver_name || '').toLowerCase().includes(q)
        || String(item.current_step_index || '').includes(q)
        || stepText.includes(q)
      );
    });
  }

  filterPartRouteSnapshots = (snapshots) => {
    return snapshots.filter((item) => {
      if (!this.state.searchTerm) return true;
      const q = this.state.searchTerm.toLowerCase();
      const stepText = (item.route_steps || [])
        .map((step) => `${step.action || ''} ${step.booking_id || ''} ${step.location_name || ''}`)
        .join(' ')
        .toLowerCase();

      return (
        String(item.vehicle_id || '').includes(q)
        || (item.vehicle_registration_no || '').toLowerCase().includes(q)
        || (item.driver_name || '').toLowerCase().includes(q)
        || String(item.snapshot_date || '').toLowerCase().includes(q)
        || stepText.includes(q)
      );
    });
  }

  isClusterRow = (row) => Array.isArray(row.bookings) && row.bookings.length > 0;

  toggleClusterBookingExpand = (bookingId) => {
    const id = Number(bookingId);
    this.setState((prev) => {
      const expanded = new Set(prev.expandedBookings);
      if (expanded.has(id)) expanded.delete(id);
      else expanded.add(id);
      return { expandedBookings: Array.from(expanded) };
    });
  };

  isBookingExpanded = (bookingId) => {
    return this.state.expandedBookings.includes(Number(bookingId));
  };

  renderClusterBookingRouteDetail = (booking) => {
    if (!booking || !this.isBookingExpanded(booking.booking_id)) return null;
    const pt = this.formatDate(booking.pickup_time);
    return (
      <div className={s.clusterBookingDetail}>
        <div className={s.clusterBookingDetailRow}>
          <span className={s.locationIcon}>🕐</span>
          <span>{pt.date} {pt.time}</span>
        </div>
        <div className={s.clusterBookingDetailRow}>
          <span className={s.locationIcon}>📍</span>
          <span>{booking.pickup_location || 'N/A'}</span>
        </div>
        <div className={s.clusterBookingDetailRow}>
          <span className={s.locationIcon}>🎯</span>
          <span>{booking.drop_location || 'N/A'}</span>
        </div>
        {this.renderMapsLink(getTripRouteStops(booking), booking.drop_location ? 1 : 0)}
        {booking.material_description && (
          <div className={s.clusterBookingDetailRow}>
            <span className={s.locationIcon}>📦</span>
            <span>Materials: {booking.material_description}</span>
          </div>
        )}
        {booking.is_assigned && booking.assigned_vehicle_id != null && (
          <div className={s.clusterBookingDetailRow}>
            <span className={s.locationIcon}>✓</span>
            <span>
              Assigned to V{booking.assigned_vehicle_id}
              {booking.assigned_driver_name ? ` · ${booking.assigned_driver_name}` : ''}
              {booking.assignment_state ? ` (${booking.assignment_state})` : ''}
            </span>
          </div>
        )}
      </div>
    );
  };

  renderClusterBookingIdCell = (booking) => {
    const expanded = this.isBookingExpanded(booking.booking_id);
    const assigned = booking.is_assigned && booking.assigned_vehicle_id != null;
    return (
      <div key={`bid-${booking.booking_id}`} className={s.clusterBookingIdBlock}>
        <button
          type="button"
          className={`${s.clusterBookingIdItem} ${expanded ? s.clusterBookingIdItemExpanded : ''}`}
          onClick={() => this.toggleClusterBookingExpand(booking.booking_id)}
        >
          <i className="fa fa-bookmark" />
          {booking.booking_id}
        </button>
        {assigned && (
          <span className={s.clusterBookingAssignedTag}>
            → V{booking.assigned_vehicle_id}
          </span>
        )}
        {this.renderClusterBookingRouteDetail(booking)}
      </div>
    );
  };

  renderClusterBestVehicle = (row) => {
    const vid = row.cluster_vehicle_id ?? row.marked_vehicle_id;
    if (vid == null) return <span className={s.unassigned}>—</span>;
    const driver = row.cluster_driver_name || row.marked_driver_name || '';
    const vtype = row.cluster_vehicle_type || row.marked_vehicle_type || row.vehicle_type || '';
    const assignedVid = row.cluster_assigned_vehicle_id;
    const allAssigned = row.cluster_all_assigned && assignedVid != null;
    const matchesBest = allAssigned && Number(assignedVid) === Number(vid);
    return (
      <div className={s.clusterBestVehicleCell}>
        <span className={`${s.bestVehicleBadge} ${matchesBest ? s.bestVehicleBadgeAssigned : ''}`}>
          V{vid}
          {driver ? ` · ${driver}` : ''}
          {vtype ? ` · ${vtype}` : ''}
        </span>
        {matchesBest && (
          <span className={s.clusterAssignedBanner}>
            All bookings assigned to this vehicle
          </span>
        )}
        {row.cluster_partial_assigned && (
          <span className={s.clusterPartialAssignedBanner}>
            Some bookings in cluster already assigned
          </span>
        )}
      </div>
    );
  };

  renderClusterSuitableButtons = (row, bookingIdsForAssign) => {
    if (this.state.clusterIsPreview) {
      return (
        <span className={s.unassigned}>
          Preview only — switch to today to assign
        </span>
      );
    }
    const vehicles = row.suitable_vehicles || [];
    const anyClusterAssign = this.state.clusterAssigningKey != null;
    const bookingIds = bookingIdsForAssign || (
      this.isClusterRow(row)
        ? row.bookings.map((b) => b.booking_id)
        : row.booking_id
          ? [row.booking_id]
          : []
    );

    if (row.cluster_all_assigned && row.cluster_assigned_vehicle_id != null) {
      const drv = row.cluster_assigned_driver_name || '';
      return (
        <span className={s.clusterAssignedBanner}>
          All assigned to V{row.cluster_assigned_vehicle_id}
          {drv ? ` · ${drv}` : ''}
        </span>
      );
    }

    const singleAssigned =
      !this.isClusterRow(row) && row.is_assigned && row.assigned_vehicle_id != null;
    if (singleAssigned) {
      return (
        <span className={s.clusterAssignedBanner}>
          Assigned to V{row.assigned_vehicle_id}
          {row.assigned_driver_name ? ` · ${row.assigned_driver_name}` : ''}
        </span>
      );
    }

    if (vehicles.length === 0) {
      return <span className={s.unassigned}>No vehicles found</span>;
    }

    return (
      <div className={s.clusterVehicleButtons}>
        {vehicles.map((v) => {
          const assignKey = bookingIds.length === 1
            ? `${bookingIds[0]}-${v.vehicle_id}`
            : `cluster-${bookingIds.join('-')}-${v.vehicle_id}`;
          const loading = this.state.clusterAssigningKey === assignKey;
          const assignAll = bookingIds.length > 1;
          return (
            <Button
              key={assignKey}
              type="button"
              size="sm"
              color="success"
              outline
              className={s.clusterAssignBtn}
              disabled={anyClusterAssign}
              title={`Assign ${assignAll ? `bookings ${bookingIds.join(', ')}` : `booking #${bookingIds[0]}`} to vehicle #${v.vehicle_id}${v.driver_name ? ` — ${v.driver_name}` : ''}. ${v.vehicle_type || ''} @ (${Number(v.current_lat).toFixed(4)}, ${Number(v.current_lng).toFixed(4)})${v.gap_assignment ? ' — gap slot' : ''}`}
              onClick={() => {
                if (assignAll) {
                  this.assignClusterVehicleBatch(bookingIds, v.vehicle_id);
                } else {
                  this.assignClusterVehicle(bookingIds[0], v.vehicle_id);
                }
              }}
            >
              {loading ? 'Assigning…' : `V${v.vehicle_id}`}
              {v.driver_name ? ` · ${v.driver_name}` : ''}
              {v.vehicle_type ? ` · ${v.vehicle_type}` : ''}
              {v.gap_assignment ? ' · gap' : ''}
            </Button>
          );
        })}
      </div>
    );
  };

  filterClusterBookings = (rows) => {
    return (rows || []).filter((row) => {
      if (!this.state.searchTerm) return true;
      const q = this.state.searchTerm.toLowerCase();
      const vehicleIds = (row.suitable_vehicles || [])
        .map((v) => String(v.vehicle_id || ''))
        .join(' ');
      const types = (row.suitable_vehicles || [])
        .map((v) => String(v.vehicle_type || ''))
        .join(' ')
        .toLowerCase();
      const driverNames = (row.suitable_vehicles || [])
        .map((v) => String(v.driver_name || '').toLowerCase())
        .join(' ');
      const clusterMeta = [
        row.cluster_driver_name,
        row.cluster_vehicle_id,
        row.marked_driver_name,
        row.marked_vehicle_id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const nestedBookings = this.isClusterRow(row) ? row.bookings : [row];
      const nestedMatch = nestedBookings.some(
        (b) =>
          String(b.booking_id || '').includes(q)
          || (b.pickup_location || '').toLowerCase().includes(q)
          || (b.drop_location || '').toLowerCase().includes(q),
      );
      return (
        nestedMatch
        || (clusterMeta && clusterMeta.includes(q))
        || String(row.booking_id || '').includes(q)
        || (row.pickup_location || '').toLowerCase().includes(q)
        || (row.drop_location || '').toLowerCase().includes(q)
        || vehicleIds.includes(q)
        || types.includes(q)
        || driverNames.includes(q)
      );
    });
  }

  formatDate = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
      time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    };
  }

  getPartStatusClass = (status) => {
    const value = (status || '').toLowerCase();
    if (value === 'loaded') return 'loaded';
    if (value === 'completed') return 'completed';
    return 'pending';
  }

  formatPartStatus = (status) => {
    const value = (status || '').toLowerCase();
    if (value === 'loaded') return 'Loaded';
    if (value === 'completed') return 'Completed';
    return 'Pending Pickup';
  }

  getPartTripStopCounts = (item) => {
    const pickupStops = Array.isArray(item?.pickup_stops) ? item.pickup_stops : [];
    const dropStops = Array.isArray(item?.drop_stops) ? item.drop_stops : [];

    const pickupStopCount = Number(item?.pickup_stop_count);
    const dropStopCount = Number(item?.drop_stop_count);

    const pickupCount = Number.isInteger(pickupStopCount) && pickupStopCount > 0
      ? pickupStopCount
      : pickupStops.length > 0
        ? pickupStops.length
        : item?.pickup_location
          ? 1
          : 0;

    const dropCount = Number.isInteger(dropStopCount) && dropStopCount > 0
      ? dropStopCount
      : dropStops.length > 0
        ? dropStops.length
        : item?.drop_location
          ? 1
          : 0;

    return { pickupCount, dropCount };
  }

  getRouteStopCountsByBooking = (steps) => {
    const countsByBooking = {};
    (steps || []).forEach((step) => {
      const bookingId = String(step?.booking_id || '');
      if (!bookingId) return;

      if (!countsByBooking[bookingId]) {
        countsByBooking[bookingId] = { pickupCount: 0, dropCount: 0 };
      }

      const action = String(step?.action || '').toLowerCase();
      if (action === 'pickup') {
        countsByBooking[bookingId].pickupCount += 1;
      } else if (action === 'dropoff' || action === 'drop') {
        countsByBooking[bookingId].dropCount += 1;
      }
    });
    return countsByBooking;
  }

  formatStopSummary = (pickupCount, dropCount) => {
    return `P${pickupCount || 0}/D${dropCount || 0}`;
  }

  renderMapsLink = (stops, dropCount = 0) => {
    const mapsUrl = buildGoogleMapsRouteUrl(stops);
    const mapsLabel = getMapsRouteLabel(stops, dropCount);
    if (!mapsUrl || !mapsLabel) return null;
    return (
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={s.routeMapsLink}
        title={mapsLabel}
        onClick={(e) => e.stopPropagation()}
      >
        <i className="fa fa-external-link" aria-hidden="true" />
        {mapsLabel}
      </a>
    );
  }

  getDriverInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }

  getStatusClass = (status) => {
    const statusMap = {
      assigned: 'assigned',
      morning_confirmed: 'Accepted',
      night_confirmed: 'Accepted',
      en_route_pickup: 'en_route_pickup',
      at_pickup: 'at_pickup',
      loaded: 'loaded',
      trip_started: 'trip_started',
      en_route_drop: 'en_route_drop',
      completed: 'completed',
      trip_completed: 'trip_completed',
      cancelled: 'cancelled',
    };
    return statusMap[status] || '';
  }

  formatStatus = (status) => {
    if (!status) return 'Unknown';
    // Map morning_confirmed to Accepted for display
    if (status === 'morning_confirmed' || status === 'night_confirmed') {
      return 'Accepted';
    }
    return status.replace(/_/g, ' ');
  }

  isCompletedTrip = (status) => {
    const value = (status || '').toLowerCase();
    return value === 'completed' || value === 'trip_completed';
  }

  needsManualPayPrompt = (trip) => {
    if (!this.isCompletedTrip(trip.status)) return false;
    const payAdded = Boolean(trip.ops_pay_added);
    const markedPaid = Boolean(trip.ops_marked_paid);
    return !(payAdded && markedPaid);
  }

  isTripPayAdded = (trip) => Boolean(trip.ops_pay_added)

  isTripMarkedPaid = (trip) => Boolean(trip.ops_marked_paid)

  openLabourPayModal = (trip) => {
    this.setState({
      labourPayModalOpen: true,
      labourPayTrip: trip,
      tripPayAmount: trip.estimated_cost != null ? String(trip.estimated_cost) : '',
      labourPayAmount: trip.labour_pay != null ? String(trip.labour_pay) : '0',
    });
  }

  toggleLabourPayModal = () => {
    this.setState((prev) => ({
      labourPayModalOpen: !prev.labourPayModalOpen,
      labourPayTrip: prev.labourPayModalOpen ? null : prev.labourPayTrip,
    }));
  }

  openMarkPaidModal = (trip) => {
    if (this.isTripMarkedPaid(trip)) {
      toast.info('Trip is already marked as paid');
      return;
    }
    this.setState({
      markPaidModalOpen: true,
      markPaidTrip: trip,
    });
  }

  toggleMarkPaidModal = () => {
    this.setState((prev) => ({
      markPaidModalOpen: !prev.markPaidModalOpen,
      markPaidTrip: prev.markPaidModalOpen ? null : prev.markPaidTrip,
    }));
  }

  handleMarkAsPaid = async (paymentMode) => {
    const { markPaidTrip } = this.state;
    if (!markPaidTrip?.trip_id) {
      toast.error('Trip not selected');
      return;
    }
    if (paymentMode !== 'cash' && paymentMode !== 'online') {
      toast.error('Please select a payment mode');
      return;
    }

    this.setState({ isMarkingPaid: true });
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const response = await fetch(`${config.opsApiBase}/trips/${markPaidTrip.trip_id}/mark-paid`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_paid_cash: paymentMode === 'cash' }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to mark trip as paid');
      }

      toast.success(`Trip ${markPaidTrip.trip_id} marked as paid (${paymentMode})`);
      this.setState({
        markPaidModalOpen: false,
        markPaidTrip: null,
        isMarkingPaid: false,
      });
      await this.fetchTrips();
    } catch (error) {
      console.error('Failed to mark trip as paid:', error);
      toast.error(error.message || 'Failed to mark trip as paid');
      this.setState({ isMarkingPaid: false });
    }
  }

  handleUpdateLabourPay = async () => {
    const { labourPayTrip, tripPayAmount, labourPayAmount } = this.state;
    if (!labourPayTrip?.trip_id) {
      toast.error('Trip not selected');
      return;
    }

    const parsedTripPay = Number(tripPayAmount);
    const parsedLabourPay = Number(labourPayAmount);

    if (Number.isNaN(parsedTripPay) || parsedTripPay < 0) {
      toast.error('Please enter a valid trip pay amount');
      return;
    }
    if (Number.isNaN(parsedLabourPay) || parsedLabourPay < 0) {
      toast.error('Please enter a valid labour pay amount');
      return;
    }

    this.setState({ isUpdatingLabourPay: true });
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const response = await fetch(`${config.opsApiBase}/trips/${labourPayTrip.trip_id}/labour-pay`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          trip_pay: parsedTripPay,
          labour_pay: parsedLabourPay,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update pay');
      }

      toast.success(`Pay saved for trip ${labourPayTrip.trip_id}`);
      this.setState({
        labourPayModalOpen: false,
        labourPayTrip: null,
        isUpdatingLabourPay: false,
      });
      await this.fetchTrips();
    } catch (error) {
      console.error('Failed to update pay:', error);
      toast.error(error.message || 'Failed to update pay');
      this.setState({ isUpdatingLabourPay: false });
    }
  }

  sendLocationTemplate = async (trip, locationType, dropIndex = null, customLocation = null) => {
    if (!trip?.trip_id) return;
    if (!trip.driver_id) {
      toast.warning('No driver assigned to this trip');
      return;
    }

    const targetLoc = locationType === 'pickup'
      ? trip.pickup_location
      : (customLocation || trip.drop_location);

    if (!targetLoc || targetLoc === 'N/A') {
      toast.warning(`Trip #${trip.trip_id} has no ${locationType} location`);
      return;
    }

    const label = locationType === 'pickup' ? 'pickup' : 'drop';
    const driverLabel = trip.driver_name ? `${trip.driver_name} (ID: ${trip.driver_id})` : `driver #${trip.driver_id}`;
    const confirmed = window.confirm(`Send ${label} location template to ${driverLabel} on WhatsApp?\n\nLocation:\n${targetLoc}`);
    if (!confirmed) return;

    const sendingKey = `${trip.trip_id}-${locationType}${dropIndex != null ? `-${dropIndex}` : ''}`;
    this.setState({ sendingLocationKey: sendingKey });

    try {
      const payload = {
        location_type: locationType,
        drop_index: dropIndex,
        custom_location: customLocation,
      };
      const response = await axios.post(`/trips/${trip.trip_id}/send-location-template`, payload);
      if (response.data && response.data.success) {
        toast.success(response.data.message || `Sent ${label} location to driver`);
      } else {
        toast.error(response.data?.detail || response.data?.message || 'Failed to send template');
      }
    } catch (error) {
      console.error('Failed to send location template:', error);
      const msg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to send location template';
      toast.error(msg);
    } finally {
      this.setState({ sendingLocationKey: null });
    }
  }

  openAddVehicleModal = (trip) => {
    this.setState({
      transporterVehicleModal: {
        isOpen: true,
        trip,
        activeTab: 'assign',
        vehicles: [],
        isLoadingVehicles: true,
        assigningVehicleId: null,
        error: null,
      },
    }, () => {
      this.fetchTransporterVehicles(trip.transporter_id);
    });
  };

  closeAddVehicleModal = () => {
    this.setState((prev) => ({
      transporterVehicleModal: {
        ...prev.transporterVehicleModal,
        isOpen: false,
        trip: null,
        vehicles: [],
        isLoadingVehicles: false,
        assigningVehicleId: null,
        error: null,
      },
    }));
  };

  setAddVehicleModalTab = (tab) => {
    this.setState((prev) => ({
      transporterVehicleModal: {
        ...prev.transporterVehicleModal,
        activeTab: tab,
      },
    }));
  };

  fetchTransporterVehicles = async (transporterId) => {
    if (!transporterId) {
      this.setState((prev) => ({
        transporterVehicleModal: {
          ...prev.transporterVehicleModal,
          isLoadingVehicles: false,
          error: 'No transporter ID associated with this trip',
        },
      }));
      return;
    }

    try {
      let vehicles = [];
      try {
        const res = await axios.get(`/transporters/${transporterId}/vehicles`);
        if (res.data && Array.isArray(res.data.vehicles)) {
          vehicles = res.data.vehicles;
        }
      } catch (e) {
        // Fallback to vehicle-drivers
        const fallbackRes = await axios.get('/vehicle-drivers');
        const allVehicles = fallbackRes.data?.data || [];
        vehicles = allVehicles.filter(
          (v) => Number(v.transporter_id) === Number(transporterId)
        );
      }

      this.setState((prev) => ({
        transporterVehicleModal: {
          ...prev.transporterVehicleModal,
          vehicles,
          isLoadingVehicles: false,
          error: null,
        },
      }));
    } catch (err) {
      this.setState((prev) => ({
        transporterVehicleModal: {
          ...prev.transporterVehicleModal,
          isLoadingVehicles: false,
          error: 'Failed to load vehicles for this transporter',
        },
      }));
    }
  };

  handleAssignVehicleToBooking = async (vehicle) => {
    const { trip } = this.state.transporterVehicleModal;
    if (!trip || !vehicle) return;

    const operatorName =
      localStorage.getItem('user_name') ||
      localStorage.getItem('username') ||
      'Unknown Operator';
    const token = localStorage.getItem('token') || '';

    this.setState((prev) => ({
      transporterVehicleModal: {
        ...prev.transporterVehicleModal,
        assigningVehicleId: vehicle.vehicle_id,
        error: null,
      },
    }));

    try {
      const endpoint = `${config.dispatchApiBase}/manual-assignment`;
      const payload = {
        booking_id: parseInt(trip.booking_id, 10),
        vehicle_id: parseInt(vehicle.vehicle_id, 10),
        assigned_by: operatorName,
        source: 'trips_page',
        confirmed: true,
        warnings_acknowledged: true,
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.detail || data.message || 'Failed to assign vehicle');
      }

      toast.success(
        `Vehicle ${vehicle.vehicle_id} successfully assigned to booking #${trip.booking_id}!`
      );
      this.closeAddVehicleModal();
      this.fetchTrips();
    } catch (err) {
      toast.error(err.message || 'Failed to assign vehicle');
      this.setState((prev) => ({
        transporterVehicleModal: {
          ...prev.transporterVehicleModal,
          assigningVehicleId: null,
          error: err.message,
        },
      }));
    }
  };

  handleGoToOnboard = () => {
    const { trip } = this.state.transporterVehicleModal;
    const phone = (trip && trip.transporter_phone) || '';
    const url = `/app/drivers/new?type=transporter&phone=${encodeURIComponent(phone)}`;
    this.closeAddVehicleModal();
    if (this.props.history) {
      this.props.history.push(url);
    } else {
      window.location.href = url;
    }
  };

  render() {
    const {
      searchTerm,
      activeTab,
      trips,
      clusterBookings,
      clusterUpdatedAt,
      partTrips,
      partRoutes,
      partRouteSnapshots,
      clearingRouteVehicleId,
      completingRouteVehicleId,
      completingAndSendingRouteVehicleId,
      isFetching,
      isPageFetching,
      isBackgroundRefreshing,
      hasLoadedLive,
      hasLoadedPart,
      hasLoadedRoutes,
      hasLoadedCluster,
      error,
      currentPage,
      pageInput,
      itemsPerPage,
      nextRefreshIn,
      totalTrips,
      totalPartTrips,
      totalPartRoutes,
      autoAssignPaused,
      togglingPause,
      clusterAssigningKey,
      clusterDates,
      clusterSelectedDate,
      clusterToday,
      clusterIsPreview,
      labourPayModalOpen,
      labourPayTrip,
      tripPayAmount,
      labourPayAmount,
      isUpdatingLabourPay,
      markPaidModalOpen,
      markPaidTrip,
      isMarkingPaid,
      transporterVehicleModal,
    } = this.state;
    const filteredTrips = this.filterTrips(trips);
    const filteredPartTrips = this.filterPartTrips(partTrips);
    const filteredPartRoutes = this.filterPartRoutes(partRoutes);
    const filteredPartRouteSnapshots = this.filterPartRouteSnapshots(partRouteSnapshots);
    const filteredClusterRows = this.filterClusterBookings(clusterBookings);
    const clusterPaginated = filteredClusterRows.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage,
    );

    // totalTrips comes from the server (already filtered by date + city)
    // so totalPages accurately reflects the filtered result set
    const activeTotal = activeTab === 'part'
      ? totalPartTrips
      : activeTab === 'routes'
        ? totalPartRoutes
        : activeTab === 'cluster'
          ? filteredClusterRows.length
          : totalTrips;
    const totalPages = Math.ceil(activeTotal / itemsPerPage) || 1;
    const paginatedTrips = activeTab === 'part'
      ? filteredPartTrips
      : activeTab === 'routes'
        ? filteredPartRoutes
        : activeTab === 'cluster'
          ? clusterPaginated
          : filteredTrips;

    const hasData = activeTab === 'part'
      ? (hasLoadedPart || partTrips.length > 0)
      : activeTab === 'routes'
        ? (hasLoadedRoutes || partRoutes.length > 0 || partRouteSnapshots.length > 0)
        : activeTab === 'cluster'
          ? (hasLoadedCluster || clusterBookings.length > 0)
          : (hasLoadedLive || trips.length > 0);

    return (
      <div className={s.tripListContainer}>
        <Breadcrumb>
          <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
          <BreadcrumbItem active>
            {activeTab === 'part' ? 'Part Trips' : activeTab === 'routes' ? 'Part Routes' : activeTab === 'cluster' ? 'Cluster' : 'Live Trips'}
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Page Header */}
        <div className={s.pageHeader}>
          <div className={s.pageTitle}>
            <div className={s.titleIcon}>
              <i className="fa fa-truck" />
            </div>
            <h1>Live Trip Management</h1>
          </div>
          <div className={s.headerActions}>
            <Link to="/app/customers/new-booking" className={s.btnNewBooking}>
              <i className="fa fa-plus" />
              New Booking
            </Link>
            <Link to="/app/trips/manual-assignments" className={s.btnManualAssign}>
              <i className="fa fa-hand-paper-o" />
              Manual Assignments
            </Link>
            <Link to="/app/unlocked-bookings" className={s.btnUnlocked}>
              <i className="fa fa-unlock" />
              Unlocked Bookings
            </Link>
            <Button
              className={autoAssignPaused ? s.btnPausedActive : s.btnPause}
              onClick={this.toggleAutoAssign}
              disabled={togglingPause}
              title={autoAssignPaused ? 'Auto-assignment is PAUSED — click to resume' : 'Click to pause the 15-min auto-assignment cycle'}
            >
              <i className={`fa ${autoAssignPaused ? 'fa-play' : 'fa-pause'}`} />
              {togglingPause ? '...' : autoAssignPaused ? 'Resume Auto Assign' : 'Pause Auto Assign'}
            </Button>
            <Button 
              className={s.btnRefresh} 
              onClick={
                activeTab === 'part'
                  ? this.fetchPartTrips
                  : activeTab === 'routes'
                    ? this.fetchPartRoutes
                    : activeTab === 'cluster'
                      ? this.fetchClusterSuitable
                      : this.fetchTrips
              }
              title={`Auto-refreshing in ${nextRefreshIn}s`}
            >
              <i className={`fa fa-refresh${isBackgroundRefreshing ? ' fa-spin' : ''}`} />
              Refresh {!isFetching && `(${nextRefreshIn}s)`}
            </Button>
            {isBackgroundRefreshing && (
              <span style={{
                marginLeft: '8px',
                fontSize: '11px',
                color: '#6c757d',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                opacity: 0.85,
              }}>
                <span style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: '#0d6efd',
                  display: 'inline-block',
                  animation: 'bgSyncPulse 1s ease-in-out infinite',
                }} />
                Syncing…
              </span>
            )}
          </div>
        </div>

        <div className={s.tabButtons}>
          <button
            className={`${s.tabButton} ${activeTab === 'live' ? s.activeTab : ''}`}
            onClick={() => this.handleTabChange('live')}
          >
            Live Trips
          </button>
          <button
            className={`${s.tabButton} ${activeTab === 'cluster' ? s.activeTab : ''}`}
            onClick={() => this.handleTabChange('cluster')}
          >
            Cluster
          </button>
          <button
            className={`${s.tabButton} ${activeTab === 'part' ? s.activeTab : ''}`}
            onClick={() => this.handleTabChange('part')}
          >
            Part Trips
          </button>
          <button
            className={`${s.tabButton} ${activeTab === 'routes' ? s.activeTab : ''}`}
            onClick={() => this.handleTabChange('routes')}
          >
            Part Routes
          </button>
        </div>

        {/* Search Section */}
        <div className={s.searchSection}>
          <Row>
            {activeTab === 'cluster' && clusterDates.length > 0 && (
              <Col md={3} className="mb-2 mb-md-0">
                <Input
                  type="select"
                  value={clusterSelectedDate || clusterToday || ''}
                  onChange={this.handleClusterDateChange}
                  aria-label="Cluster pickup date"
                >
                  {clusterDates.map((d) => (
                    <option key={d} value={d}>
                      {d === clusterToday ? `Today (${d})` : d}
                    </option>
                  ))}
                </Input>
              </Col>
            )}
            <Col md={activeTab === 'cluster' && clusterDates.length > 0 ? 9 : 6}>
              <InputGroup>
                <Input
                  className={s.searchInput}
                  placeholder={
                    activeTab === 'routes'
                      ? 'Search by vehicle, driver, booking, or route step...'
                      : activeTab === 'cluster'
                        ? 'Search by booking ID, location, or suitable vehicle ID...'
                        : 'Search trips by ID, booking ID, driver, or location...'
                  }
                  value={searchTerm}
                  onChange={this.handleSearch}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      this.handleSearchSubmit(e);
                    }
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={this.handleClearSearch}
                    style={{
                      position: 'absolute',
                      right: '48px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: '#9ca3af',
                      cursor: 'pointer',
                      zIndex: 10,
                      padding: '4px 8px',
                      fontSize: '14px',
                    }}
                    title="Clear search"
                  >
                    <i className="fa fa-times" />
                  </button>
                )}
                <InputGroupAddon addonType="append">
                  <Button className={s.searchButton} onClick={this.handleSearchSubmit}>
                    <i className="fa fa-search" />
                  </Button>
                </InputGroupAddon>
              </InputGroup>
            </Col>
          </Row>
        </div>

        {/* Trip Table */}
        <div className={s.tripCard}>
          {(isFetching || isPageFetching) && (
            <div className={s.loadingProgressBar} />
          )}

          {error ? (
            <div className={s.errorState}>
              <div className={s.errorIcon}>
                <i className="fa fa-exclamation-circle" />
              </div>
              <p>Error loading trips: {error}</p>
              <Button className={s.retryButton} onClick={
                activeTab === 'part'
                  ? this.fetchPartTrips
                  : activeTab === 'routes'
                    ? this.fetchPartRoutes
                    : activeTab === 'cluster'
                      ? this.fetchClusterSuitable
                      : this.fetchTrips
              }>
                <i className="fa fa-refresh mr-2" />
                Retry
              </Button>
            </div>
          ) : !hasData && isFetching ? (
            <div className={s.loadingState}>
              <div className={s.spinner} />
              <p>Loading {activeTab === 'cluster' ? 'cluster data' : 'trips'}...</p>
            </div>
          ) : !isFetching && !isPageFetching && paginatedTrips.length === 0 ? (
            <div className={s.emptyState}>
              <div className={s.emptyIcon}>
                <i className="fa fa-inbox" />
              </div>
              <h3>No {activeTab === 'part' ? 'part trips' : activeTab === 'routes' ? 'part routes' : activeTab === 'cluster' ? 'cluster rows' : 'trips'} found</h3>
              <p>There are no {activeTab === 'part' ? 'part trip assignments' : activeTab === 'routes' ? 'active redis routes' : activeTab === 'cluster' ? 'rows in the cluster snapshot for this search (snapshots update each dispatch step)' : 'trips'} matching your criteria.</p>
            </div>
          ) : (
            <div className={s.tableWrapper}>
              {activeTab === 'routes' && (
                <div className={s.routeSectionTitle}>Active Routes (Redis)</div>
              )}
              {activeTab === 'cluster' && clusterIsPreview && (
                <div className={s.routeSectionTitle}>
                  Preview cluster for {clusterSelectedDate || clusterToday} — read only. Live assignment and dispatch apply on that day when it becomes today.
                </div>
              )}
              {activeTab === 'cluster' && !clusterIsPreview && clusterUpdatedAt && (
                <div className={s.routeSectionTitle}>
                  Clusters group bookings by algorithm best vehicle when auto-assign is paused (snapshot {clusterUpdatedAt}). Click a booking ID for pickup time, route, and assignment status; click a suitable vehicle to manual-assign.
                </div>
              )}
              <table className={`${s.tripTable} ${isPageFetching ? s.tableLoading : ''}`}>
                <thead>
                  {activeTab === 'part' ? (
                    <tr>
                      <th>Assignment ID</th>
                      <th>Booking ID</th>
                      <th>Driver</th>
                      <th>Pickup</th>
                      <th>Drop</th>
                      <th>Price</th>
                      <th>Status</th>
                      <th>Assigned</th>
                      <th>Loaded</th>
                      <th>Completed</th>
                    </tr>
                  ) : activeTab === 'routes' ? (
                    <tr>
                      <th>Vehicle</th>
                      <th>Driver</th>
                      <th>Current Step</th>
                      <th>Total Steps</th>
                      <th>Completed Distance</th>
                      <th>Redis TTL</th>
                      <th>Updated At</th>
                      <th>Route Steps (PDP)</th>
                      <th>Actions</th>
                    </tr>
                  ) : activeTab === 'cluster' ? (
                    <tr>
                      <th>Booking ID(s)</th>
                      <th>Class</th>
                      <th>Best vehicle</th>
                      <th>Suitable vehicles</th>
                    </tr>
                  ) : (
                    <tr>
                      <th>Trip ID</th>
                      <th>Booking ID</th>
                      <th>Driver</th>
                      <th>Route</th>
                      <th>Pickup Time</th>
                      <th>Pay</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {activeTab === 'part' ? paginatedTrips.map(item => {
                    const assigned = this.formatDate(item.assigned_at);
                    const loaded = this.formatDate(item.loaded_at);
                    const completed = this.formatDate(item.completed_at);
                    const { pickupCount, dropCount } = this.getPartTripStopCounts(item);
                    const showStopSummary = pickupCount > 1 || dropCount > 1;
                    return (
                      <tr key={`part-${item.id}`}>
                        <td><span className={s.tripId}>{item.id}</span></td>
                        <td>
                          <span className={s.bookingId}>
                            <i className="fa fa-bookmark" />
                            {item.booking_id || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <div className={s.driverCell}>
                            <div className={s.driverAvatar}>{this.getDriverInitials(item.driver_name)}</div>
                            <span className={item.driver_name ? s.driverName : s.unassigned}>{item.driver_name || 'Unassigned'}</span>
                          </div>
                        </td>
                        <td className={s.locationCell}>
                          <div className={s.locationText}>
                            <span className={s.locationIcon}>📍</span>
                            <span>{item.pickup_location || 'N/A'}</span>
                          </div>
                          {showStopSummary && (
                            <div className={s.stopMeta}>{this.formatStopSummary(pickupCount, dropCount)}</div>
                          )}
                        </td>
                        <td className={s.locationCell}>
                          <div className={s.locationText}>
                            <span className={s.locationIcon}>🎯</span>
                            <span>{item.drop_location || 'N/A'}</span>
                          </div>
                          {showStopSummary && (
                            <div className={s.stopMeta}>{this.formatStopSummary(pickupCount, dropCount)}</div>
                          )}
                          {this.renderMapsLink(getTripRouteStops(item), dropCount)}
                        </td>
                        <td>
                          <span className={s.bookingId}>₹{item.price != null ? Number(item.price).toFixed(2) : '0.00'}</span>
                        </td>
                        <td>
                          <span className={`${s.statusBadge} ${s[this.getPartStatusClass(item.status)]}`}>
                            {this.formatPartStatus(item.status)}
                          </span>
                        </td>
                        <td className={s.timeCell}><div className={s.timeDate}>{assigned.date}</div><div className={s.timeHour}>{assigned.time}</div></td>
                        <td className={s.timeCell}><div className={s.timeDate}>{loaded.date}</div><div className={s.timeHour}>{loaded.time}</div></td>
                        <td className={s.timeCell}><div className={s.timeDate}>{completed.date}</div><div className={s.timeHour}>{completed.time}</div></td>
                      </tr>
                    );
                  }) : activeTab === 'routes' ? paginatedTrips.map((item) => {
                    const updated = this.formatDate(item.updated_at);
                    const stopCountsByBooking = this.getRouteStopCountsByBooking(item.steps || []);
                    return (
                      <tr key={`route-${item.vehicle_id}`}>
                        <td>
                          <div className={s.driverCell}>
                            <div className={s.driverAvatar}>#{item.vehicle_id}</div>
                            <span className={s.driverName}>{item.vehicle_registration_no || 'No Reg'}</span>
                          </div>
                        </td>
                        <td>
                          <div className={s.driverCell}>
                            <div className={s.driverAvatar}>{this.getDriverInitials(item.driver_name)}</div>
                            <span className={item.driver_name ? s.driverName : s.unassigned}>{item.driver_name || 'Unassigned'}</span>
                          </div>
                        </td>
                        <td><span className={s.bookingId}>{item.current_step_index || 'Done'}</span></td>
                        <td><span className={s.bookingId}>{item.total_steps || 0}</span></td>
                        <td><span className={s.bookingId}>{Number(item.completed_distance_km || 0).toFixed(2)} km</span></td>
                        <td><span className={s.bookingId}>{item.redis_ttl_seconds != null ? `${item.redis_ttl_seconds}s` : 'N/A'}</span></td>
                        <td className={s.timeCell}><div className={s.timeDate}>{updated.date}</div><div className={s.timeHour}>{updated.time}</div></td>
                        <td>
                          <div className={s.routeSteps}>
                            {(item.steps || []).map((step) => {
                              const bookingKey = String(step?.booking_id || '');
                              const bookingStopCounts = stopCountsByBooking[bookingKey] || { pickupCount: 0, dropCount: 0 };
                              return (
                              <div key={`v-${item.vehicle_id}-s-${step.step}`} className={`${s.routeStepItem} ${step.completed ? s.routeStepDone : s.routeStepPending}`}>
                                #{step.step} • {(step.action || '').toUpperCase()} • B{step.booking_id} • {this.formatStopSummary(bookingStopCounts.pickupCount, bookingStopCounts.dropCount)} • {step.location_name || 'N/A'}
                              </div>
                              );
                            })}
                          </div>
                          {this.renderMapsLink(
                            getPartRouteStops(item),
                            (item.steps || []).filter((step) => {
                              const action = String(step?.action || '').toLowerCase();
                              return action === 'dropoff' || action === 'drop';
                            }).length
                          )}
                        </td>
                        <td>
                          <div className={s.routeActions}>
                            <Button
                              className={s.completeStepBtn}
                              onClick={() => this.completePartRouteStep(item, false)}
                              disabled={completingRouteVehicleId === item.vehicle_id || !item.current_step_index}
                            >
                              {completingRouteVehicleId === item.vehicle_id ? 'Completing...' : 'Complete Step'}
                            </Button>
                            <Button
                              className={s.completeAndSendBtn}
                              onClick={() => this.completePartRouteStep(item, true)}
                              disabled={completingAndSendingRouteVehicleId === item.vehicle_id || !item.current_step_index}
                            >
                              {completingAndSendingRouteVehicleId === item.vehicle_id ? 'Sending...' : 'Complete + Send Msg'}
                            </Button>
                            <Button
                              className={s.clearRouteBtn}
                              onClick={() => this.clearPartRoute(item.vehicle_id)}
                              disabled={clearingRouteVehicleId === item.vehicle_id}
                            >
                              {clearingRouteVehicleId === item.vehicle_id ? 'Clearing...' : 'Clear Route'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  }) : activeTab === 'cluster' ? paginatedTrips.map((row) => {
                    const isCluster = this.isClusterRow(row);
                    const rowKey = isCluster
                      ? `cluster-v${row.cluster_vehicle_id}`
                      : `cluster-b${row.booking_id}`;
                    return (
                      <tr key={rowKey}>
                        <td>
                          {isCluster ? (
                            <div className={s.clusterBookingIdList}>
                              {row.bookings.map((b) => this.renderClusterBookingIdCell(b))}
                            </div>
                          ) : (
                            this.renderClusterBookingIdCell(row)
                          )}
                        </td>
                        <td>
                          <span className={s.bookingId}>
                            {row.cluster_vehicle_type || row.vehicle_type || '—'}
                          </span>
                        </td>
                        <td>{this.renderClusterBestVehicle(row)}</td>
                        <td>{this.renderClusterSuitableButtons(row)}</td>
                      </tr>
                    );
                  }) : paginatedTrips.map(trip => {
                    const pickupTime = this.formatDate(trip.pickup_time);
                    const needsPay = this.needsManualPayPrompt(trip);
                    const payAdded = this.isTripPayAdded(trip);
                    const markedPaid = this.isTripMarkedPaid(trip);
                    const estimatedCost = Number(trip.estimated_cost) || 0;
                    const labourPay = Number(trip.labour_pay) || 0;
                    const totalDriverPay = estimatedCost + labourPay;
                    const hasPay = (trip.estimated_cost != null && Number(trip.estimated_cost) > 0) || (trip.labour_pay != null && Number(trip.labour_pay) > 0);

                    return (
                      <tr
                        key={trip.trip_id}
                        className={needsPay ? s.needsManualPayRow : undefined}
                      >
                        <td>
                          <span className={s.tripId}>{trip.trip_id}</span>
                          {needsPay && (
                            <div className={s.manualPayHeadline}>
                              Add the driver pay as well as make it as paid
                            </div>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {trip.booking_id ? (
                              <Link
                                to={`/app/bookings?search=${encodeURIComponent(trip.booking_id)}`}
                                className={`${s.bookingId} ${s.cellLink}`}
                                title={`Open booking ${trip.booking_id}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <i className="fa fa-bookmark" />
                                {trip.booking_id}
                              </Link>
                            ) : (
                              <span className={s.bookingId}>
                                <i className="fa fa-bookmark" />
                                N/A
                              </span>
                            )}
                            {(trip.booking_source === 'b2b') && (
                              <span className={s.b2bBadge} title="B2B Booking">
                                <i className="fa fa-star" /> B2B
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          {trip.status === 'transporter_assigned' || (trip.transporter_id && !trip.driver_id) ? (
                            <div className={s.driverCell}>
                              <div
                                style={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: '50%',
                                  backgroundColor: '#eff6ff',
                                  border: '1.5px solid #93c5fd',
                                  color: '#1d4ed8',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 16,
                                  flexShrink: 0,
                                }}
                                title={`Transporter T-${trip.transporter_id}`}
                              >
                                🚚
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                  {trip.transporter_id ? (
                                    <Link
                                      to={`/app/dispatch/vehicles?tab=transporters&search=${encodeURIComponent(`T-${trip.transporter_id}`)}`}
                                      className={`${s.cellLink} ${s.transporterLink}`}
                                      style={{
                                        padding: '2px 8px',
                                        backgroundColor: '#dbeafe',
                                        color: '#1e40af',
                                        borderRadius: 6,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        textDecoration: 'none',
                                      }}
                                      title={`Open transporter T-${trip.transporter_id}${trip.transporter_name ? ` - ${trip.transporter_name}` : ''}`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {`T-${trip.transporter_id} ${trip.transporter_name || ''}`}
                                    </Link>
                                  ) : (
                                    <span
                                      style={{
                                        padding: '2px 8px',
                                        backgroundColor: '#dbeafe',
                                        color: '#1e40af',
                                        borderRadius: 6,
                                        fontSize: 12,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {trip.transporter_name || 'Transporter'}
                                    </span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    this.openAddVehicleModal(trip);
                                  }}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    padding: '4px 10px',
                                    borderRadius: 6,
                                    border: 'none',
                                    background: '#16a34a',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                  }}
                                  title="Assign or onboard vehicle for this transporter"
                                >
                                  <i className="fa fa-plus" style={{ fontSize: 10 }} />
                                  Add Vehicle
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className={s.driverCell}>
                              <div className={s.driverAvatar}>
                                {this.getDriverInitials(trip.driver_name)}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {trip.vehicle_id ? (
                                  <Link
                                    to={`/app/dispatch/vehicles?tab=vehicles&search=${encodeURIComponent(trip.vehicle_id)}`}
                                    className={`${s.driverLink} ${s.cellLink}`}
                                    title={`Open vehicle ${trip.vehicle_id}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {trip.driver_name || 'Unassigned'}
                                    <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '4px' }}>
                                      (V-{trip.vehicle_id})
                                    </span>
                                  </Link>
                                ) : (
                                  <div>
                                    <span className={trip.driver_name ? s.driverName : s.unassigned}>
                                      {trip.driver_name || 'Unassigned'}
                                    </span>
                                    {trip.driver_id ? (
                                      <span
                                        style={{
                                          fontSize: '10px',
                                          color: '#b91c1c',
                                          backgroundColor: '#fee2e2',
                                          padding: '1px 5px',
                                          borderRadius: '4px',
                                          marginLeft: '5px',
                                          fontWeight: '600',
                                          display: 'inline-block'
                                        }}
                                        title="The vehicle assigned to this trip has been deleted"
                                      >
                                        Vehicle deleted
                                      </span>
                                    ) : null}
                                  </div>
                                )}
                                {(trip.is_transporter || trip.transporter_id) && (
                                  trip.transporter_id ? (
                                    <Link
                                      to={`/app/dispatch/vehicles?tab=transporters&search=${encodeURIComponent(`T-${trip.transporter_id}`)}`}
                                      className={`${s.cellLink} ${s.transporterLink}`}
                                      style={{
                                        alignSelf: 'flex-start',
                                        padding: '2px 6px',
                                        backgroundColor: '#dbeafe',
                                        color: '#1e40af',
                                        borderRadius: 10,
                                        fontSize: 11,
                                        fontWeight: 700,
                                      }}
                                      title={`Open transporter T-${trip.transporter_id}${trip.transporter_name ? ` - ${trip.transporter_name}` : ''}`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {`T-${trip.transporter_id}${trip.transporter_name ? ` - ${trip.transporter_name}` : ''}`}
                                    </Link>
                                  ) : (
                                    <span
                                      style={{
                                        alignSelf: 'flex-start',
                                        padding: '2px 6px',
                                        backgroundColor: '#dbeafe',
                                        color: '#1e40af',
                                        borderRadius: 10,
                                        fontSize: 11,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {trip.transporter_name || 'T'}
                                    </span>
                                  )
                                )}
                                {trip.vehicle_id ? (
                                  <Link
                                    to={`/app/dispatch/vehicles?tab=vehicles&search=${encodeURIComponent(trip.vehicle_id)}`}
                                    className={`${s.vehicleIdLink} ${s.cellLink}`}
                                    title={`Open vehicle ${trip.vehicle_id}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    (id {trip.vehicle_id})
                                  </Link>
                                ) : null}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className={s.routeCell}>
                          <div className={s.routeItem}>
                            <div className={s.routeIconPickup}>
                              <i className="fa fa-circle" />
                            </div>
                            <div className={s.routeText} title={trip.pickup_location}>
                              {trip.pickup_location || 'N/A'}
                            </div>
                          </div>
                          <div className={s.routeItem}>
                            <div className={s.routeIconDrop}>
                              <i className="fa fa-map-marker" />
                            </div>
                            <div className={s.routeText} title={trip.drop_location}>
                              {trip.drop_location || 'N/A'}
                            </div>
                          </div>
                          {(trip.additional_drops || []).map((drop) => (
                            <div className={s.routeItem} key={`${trip.trip_id}-d${drop.drop_sequence}`}>
                              <div className={s.routeIconDrop}>
                                <i className="fa fa-map-marker" />
                              </div>
                              <div className={s.routeText} title={drop.drop_location}>
                                {drop.drop_location || 'N/A'}
                              </div>
                            </div>
                          ))}
                          {this.renderMapsLink(
                            getTripRouteStops(trip),
                            (getNamedStops(trip?.drop_stops).length || (trip.drop_location ? 1 : 0))
                              + (trip.additional_drops || []).length
                          )}
                        </td>
                        <td className={s.timeCell}>
                          <div className={s.timeDate}>{pickupTime.date}</div>
                          <div className={s.timeHour}>{pickupTime.time}</div>
                        </td>
                        <td>
                          {!hasPay && !payAdded ? (
                            <button
                              type="button"
                              className={s.payAddBtn}
                              onClick={() => this.openLabourPayModal(trip)}
                              title="Click to add driver pay"
                            >
                              <i className="fa fa-plus" />
                              <span>Add Pay</span>
                            </button>
                          ) : (
                            <div
                              className={s.payBadge}
                              onClick={() => this.openLabourPayModal(trip)}
                              title="Click to edit driver pay"
                              role="button"
                              tabIndex={0}
                            >
                              <div className={s.payValueRow}>
                                <span className={s.payValue}>
                                  ₹{totalDriverPay.toFixed(2)}
                                </span>
                                <i className={`fa fa-pencil ${s.payEditIcon}`} />
                              </div>
                              {labourPay > 0 && (
                                <div className={s.payBreakdown}>
                                  Trip: ₹{estimatedCost.toFixed(0)} + Labour: ₹{labourPay.toFixed(0)}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className={`${s.statusBadge} ${s[this.getStatusClass(trip.status)]}`}>
                            {this.formatStatus(trip.status)}
                          </span>
                        </td>
                        <td>
                          <div className={s.rowActions}>
                            {needsPay && !payAdded && (
                              <button
                                type="button"
                                className={s.addPayButton}
                                onClick={() => this.openLabourPayModal(trip)}
                              >
                                <i className="fa fa-inr" />
                                Add Pay
                              </button>
                            )}
                            {needsPay && !markedPaid && (
                              <button
                                type="button"
                                className={s.markPaidButton}
                                onClick={() => this.openMarkPaidModal(trip)}
                              >
                                <i className="fa fa-check-circle" />
                                Mark as Paid
                              </button>
                            )}
                            <Link to={`/app/trips/${trip.trip_id}`} className={s.viewButton}>
                              <i className="fa fa-eye" />
                              View
                            </Link>
                            {trip.driver_id ? (
                              <>
                                <button
                                  type="button"
                                  className={s.sendPickupButton}
                                  title={`Send pickup location to ${trip.driver_name || 'driver'} via WhatsApp`}
                                  disabled={this.state.sendingLocationKey === `${trip.trip_id}-pickup`}
                                  onClick={() => this.sendLocationTemplate(trip, 'pickup')}
                                >
                                  <i className={this.state.sendingLocationKey === `${trip.trip_id}-pickup` ? "fa fa-spinner fa-spin" : "fa fa-whatsapp"} />
                                  Pickup
                                </button>
                                {trip.additional_drops && trip.additional_drops.length > 0 ? (
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      tag="button"
                                      className={s.sendDropButton}
                                      disabled={this.state.sendingLocationKey?.startsWith(`${trip.trip_id}-drop`)}
                                      title="Send drop location via WhatsApp"
                                    >
                                      <i className={this.state.sendingLocationKey?.startsWith(`${trip.trip_id}-drop`) ? "fa fa-spinner fa-spin" : "fa fa-whatsapp"} />
                                      Drop <i className="fa fa-caret-down" style={{ marginLeft: 2 }} />
                                    </DropdownToggle>
                                    <DropdownMenu right className={s.dropLocationMenu}>
                                      <DropdownItem onClick={() => this.sendLocationTemplate(trip, 'drop')}>
                                        <strong>Drop 1 (Main):</strong> {trip.drop_location || 'Main Drop'}
                                      </DropdownItem>
                                      {trip.additional_drops.map((drop) => (
                                        <DropdownItem
                                          key={`${trip.trip_id}-drop-${drop.drop_sequence}`}
                                          onClick={() => this.sendLocationTemplate(trip, 'drop', drop.drop_sequence, drop.drop_location)}
                                        >
                                          <strong>Drop {drop.drop_sequence}:</strong> {drop.drop_location}
                                        </DropdownItem>
                                      ))}
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                ) : (
                                  <button
                                    type="button"
                                    className={s.sendDropButton}
                                    title={`Send drop location to ${trip.driver_name || 'driver'} via WhatsApp`}
                                    disabled={this.state.sendingLocationKey === `${trip.trip_id}-drop`}
                                    onClick={() => this.sendLocationTemplate(trip, 'drop')}
                                  >
                                    <i className={this.state.sendingLocationKey === `${trip.trip_id}-drop` ? "fa fa-spinner fa-spin" : "fa fa-whatsapp"} />
                                    Drop
                                  </button>
                                )}
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {activeTab === 'routes' && (
                <div className={s.snapshotSection}>
                  <div className={s.routeSectionTitle}>Previous Days (DB Snapshots)</div>
                  {filteredPartRouteSnapshots.length === 0 ? (
                    <div className={s.emptySubState}>No snapshot routes for selected date filter.</div>
                  ) : (
                    <table className={s.tripTable}>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Vehicle</th>
                          <th>Driver</th>
                          <th>Completed/Total</th>
                          <th>Completed Distance</th>
                          <th>Route Steps (Snapshot)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPartRouteSnapshots.map((item) => {
                          const stopCountsByBooking = this.getRouteStopCountsByBooking(item.route_steps || []);
                          return (
                          <tr key={`snapshot-${item.id}`}>
                            <td><span className={s.bookingId}>{item.snapshot_date || 'N/A'}</span></td>
                            <td>
                              <div className={s.driverCell}>
                                <div className={s.driverAvatar}>#{item.vehicle_id}</div>
                                <span className={s.driverName}>{item.vehicle_registration_no || 'No Reg'}</span>
                              </div>
                            </td>
                            <td>
                              <div className={s.driverCell}>
                                <div className={s.driverAvatar}>{this.getDriverInitials(item.driver_name)}</div>
                                <span className={item.driver_name ? s.driverName : s.unassigned}>{item.driver_name || 'Unassigned'}</span>
                              </div>
                            </td>
                            <td><span className={s.bookingId}>{item.completed_steps || 0}/{item.total_steps || 0}</span></td>
                            <td><span className={s.bookingId}>{Number(item.completed_distance_km || 0).toFixed(2)} km</span></td>
                            <td>
                              <div className={s.routeSteps}>
                                {(item.route_steps || []).map((step) => {
                                  const bookingKey = String(step?.booking_id || '');
                                  const bookingStopCounts = stopCountsByBooking[bookingKey] || { pickupCount: 0, dropCount: 0 };
                                  return (
                                  <div key={`snapshot-${item.id}-s-${step.step}`} className={`${s.routeStepItem} ${step.completed ? s.routeStepDone : s.routeStepPending}`}>
                                    #{step.step} • {(step.action || '').toUpperCase()} • B{step.booking_id} • {this.formatStopSummary(bookingStopCounts.pickupCount, bookingStopCounts.dropCount)} • {step.location_name || 'N/A'}
                                  </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className={s.pagination}>
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => this.handlePageChange(currentPage - 1)}
                    className={s.pageBtn}
                  >
                    <i className="fa fa-chevron-left" /> Previous
                  </button>
                  <span className={s.pageInfo}>
                    Page
                    <input
                      type="number"
                      min={1}
                      max={totalPages}
                      value={pageInput}
                      onChange={this.handlePageInputChange}
                      onKeyDown={this.handlePageInputKeyDown}
                      onBlur={this.handlePageInputBlur}
                      className={s.pageInput}
                    />
                    of {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() => this.handlePageChange(currentPage + 1)}
                    className={s.pageBtn}
                  >
                    Next <i className="fa fa-chevron-right" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <Modal isOpen={labourPayModalOpen} toggle={this.toggleLabourPayModal}>
          <ModalHeader toggle={this.toggleLabourPayModal}>
            {labourPayTrip && (labourPayTrip.ops_pay_added || (labourPayTrip.estimated_cost != null && Number(labourPayTrip.estimated_cost) > 0)) ? 'Edit Pay' : 'Add Pay'}{labourPayTrip ? ` — Trip ${labourPayTrip.trip_id}` : ''}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="listTripPayAmount">Trip Pay Amount *</Label>
              <Input
                type="number"
                name="listTripPayAmount"
                id="listTripPayAmount"
                min="0"
                step="0.01"
                value={tripPayAmount}
                onChange={(e) => this.setState({ tripPayAmount: e.target.value })}
                placeholder="Enter trip pay amount"
              />
            </FormGroup>
            <FormGroup>
              <Label for="listLabourPayAmount">Labour Pay Amount *</Label>
              <Input
                type="number"
                name="listLabourPayAmount"
                id="listLabourPayAmount"
                min="0"
                step="0.01"
                value={labourPayAmount}
                onChange={(e) => this.setState({ labourPayAmount: e.target.value })}
                placeholder="Enter labour pay amount"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={this.handleUpdateLabourPay}
              disabled={isUpdatingLabourPay}
            >
              {isUpdatingLabourPay ? 'Saving...' : 'Save Pay'}
            </Button>
            {' '}
            <Button color="secondary" onClick={this.toggleLabourPayModal} disabled={isUpdatingLabourPay}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={markPaidModalOpen} toggle={this.toggleMarkPaidModal}>
          <ModalHeader toggle={this.toggleMarkPaidModal}>
            Select Payment Mode{markPaidTrip ? ` — Trip ${markPaidTrip.trip_id}` : ''}
          </ModalHeader>
          <ModalBody>
            Choose how this trip was paid.
          </ModalBody>
          <ModalFooter>
            <Button
              color="success"
              onClick={() => this.handleMarkAsPaid('cash')}
              disabled={isMarkingPaid}
            >
              {isMarkingPaid ? 'Marking...' : 'Cash'}
            </Button>
            <Button
              color="primary"
              onClick={() => this.handleMarkAsPaid('online')}
              disabled={isMarkingPaid}
            >
              {isMarkingPaid ? 'Marking...' : 'Online'}
            </Button>
            <Button color="secondary" onClick={this.toggleMarkPaidModal} disabled={isMarkingPaid}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        {/* Modal: Add Vehicle for Transporter Assigned Trip */}
        <Modal
          isOpen={transporterVehicleModal.isOpen}
          toggle={this.closeAddVehicleModal}
          size="lg"
          centered
        >
          <ModalHeader toggle={this.closeAddVehicleModal}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>🚚</span>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                  Assign or Onboard Vehicle
                </div>
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                  Booking #{transporterVehicleModal.trip?.booking_id} • Transporter: T-{transporterVehicleModal.trip?.transporter_id} {transporterVehicleModal.trip?.transporter_name || ''}
                </div>
              </div>
            </div>
          </ModalHeader>
          <ModalBody style={{ padding: '20px 24px' }}>
            {transporterVehicleModal.trip && (
              <>
                {/* Trip Route Summary Card */}
                <div
                  style={{
                    backgroundColor: '#f8fafc',
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    padding: '12px 16px',
                    marginBottom: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <span style={{ color: '#16a34a', fontWeight: 700 }}>● Pickup:</span>
                    <span style={{ color: '#334155' }}>
                      {transporterVehicleModal.trip.pickup_location || 'N/A'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <span style={{ color: '#dc2626', fontWeight: 700 }}>● Drop:</span>
                    <span style={{ color: '#334155' }}>
                      {transporterVehicleModal.trip.drop_location || 'N/A'}
                    </span>
                  </div>
                  {transporterVehicleModal.trip.transporter_phone && (
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      📞 Transporter Contact: <strong>{transporterVehicleModal.trip.transporter_phone}</strong>
                    </div>
                  )}
                </div>

                {/* Option Tabs */}
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    marginBottom: 20,
                    borderBottom: '1px solid #e2e8f0',
                    paddingBottom: 12,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => this.setAddVehicleModalTab('assign')}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      padding: '10px 16px',
                      borderRadius: 8,
                      border: transporterVehicleModal.activeTab === 'assign'
                        ? '2px solid #2563eb'
                        : '1px solid #cbd5e1',
                      backgroundColor: transporterVehicleModal.activeTab === 'assign'
                        ? '#eff6ff'
                        : '#ffffff',
                      color: transporterVehicleModal.activeTab === 'assign'
                        ? '#1d4ed8'
                        : '#475569',
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <i className="fa fa-truck" />
                    <span>Assign Vehicle ({transporterVehicleModal.vehicles.length})</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => this.setAddVehicleModalTab('onboard')}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      padding: '10px 16px',
                      borderRadius: 8,
                      border: transporterVehicleModal.activeTab === 'onboard'
                        ? '2px solid #16a34a'
                        : '1px solid #cbd5e1',
                      backgroundColor: transporterVehicleModal.activeTab === 'onboard'
                        ? '#f0fdf4'
                        : '#ffffff',
                      color: transporterVehicleModal.activeTab === 'onboard'
                        ? '#15803d'
                        : '#475569',
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <i className="fa fa-plus-circle" />
                    <span>Onboard Vehicle</span>
                  </button>
                </div>

                {/* TAB 1: Assign Vehicle */}
                {transporterVehicleModal.activeTab === 'assign' && (
                  <div>
                    {transporterVehicleModal.isLoadingVehicles ? (
                      <div style={{ textAlign: 'center', padding: '30px 0', color: '#64748b' }}>
                        <i className="fa fa-spinner fa-spin fa-2x" style={{ color: '#3b82f6', marginBottom: 10 }} />
                        <div>Loading vehicles for this transporter...</div>
                      </div>
                    ) : transporterVehicleModal.vehicles.length === 0 ? (
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '30px 20px',
                          backgroundColor: '#f8fafc',
                          borderRadius: 8,
                          border: '1px dashed #cbd5e1',
                        }}
                      >
                        <div style={{ fontSize: 32, marginBottom: 8 }}>🚚</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                          No vehicles onboarded yet
                        </div>
                        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
                          This transporter has no registered vehicles under their agency.
                        </div>
                        <Button
                          color="success"
                          onClick={this.handleGoToOnboard}
                          style={{
                            fontWeight: 600,
                            padding: '8px 18px',
                            borderRadius: 6,
                          }}
                        >
                          <i className="fa fa-plus" style={{ marginRight: 6 }} />
                          Onboard Vehicle Now
                        </Button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
                          Select a vehicle to assign to Booking #{transporterVehicleModal.trip.booking_id}:
                        </div>
                        {transporterVehicleModal.vehicles.map((v) => {
                          const isAssigning = transporterVehicleModal.assigningVehicleId === v.vehicle_id;
                          return (
                            <div
                              key={v.vehicle_id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                borderRadius: 8,
                                border: '1px solid #e2e8f0',
                                backgroundColor: '#ffffff',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                flexWrap: 'wrap',
                                gap: 12,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div
                                  style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 8,
                                    backgroundColor: '#f1f5f9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    fontSize: 13,
                                  }}
                                >
                                  #{v.vehicle_id}
                                </div>
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
                                      {v.registration_no || 'No Reg No'}
                                    </span>
                                    {v.vehicle_class && (
                                      <span
                                        style={{
                                          padding: '1px 6px',
                                          borderRadius: 4,
                                          fontSize: 11,
                                          fontWeight: 600,
                                          backgroundColor: '#e0e7ff',
                                          color: '#3730a3',
                                        }}
                                      >
                                        Class {v.vehicle_class}
                                      </span>
                                    )}
                                    {v.body_type && (
                                      <span style={{ fontSize: 12, color: '#64748b' }}>
                                        • {v.body_type}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#475569', marginTop: 2, display: 'flex', gap: 12 }}>
                                    <span>👤 {v.driver_name || 'No driver'}</span>
                                    {v.driver_phone && (
                                      <span>📞 {v.driver_phone}</span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => this.handleAssignVehicleToBooking(v)}
                                disabled={isAssigning || transporterVehicleModal.assigningVehicleId !== null}
                                style={{
                                  padding: '6px 14px',
                                  fontWeight: 600,
                                  borderRadius: 6,
                                }}
                              >
                                {isAssigning ? (
                                  <>
                                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />
                                    Assigning...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa fa-check" style={{ marginRight: 6 }} />
                                    Assign Vehicle
                                  </>
                                )}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* TAB 2: Onboard Vehicle */}
                {transporterVehicleModal.activeTab === 'onboard' && (
                  <div
                    style={{
                      padding: '24px 20px',
                      backgroundColor: '#f0fdf4',
                      borderRadius: 8,
                      border: '1px solid #bbf7d0',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: 36, marginBottom: 10 }}>➕</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#166534', marginBottom: 6 }}>
                      Onboard New Vehicle for {transporterVehicleModal.trip.transporter_name || `Transporter T-${transporterVehicleModal.trip.transporter_id}`}
                    </div>
                    <p style={{ fontSize: 13, color: '#15803d', maxWidth: 480, margin: '0 auto 20px auto' }}>
                      This will open the vehicle onboarding page pre-filled with the agency phone number (
                      <strong>{transporterVehicleModal.trip.transporter_phone || 'N/A'}</strong>
                      ), automatically attaching the new vehicle to this transporter.
                    </p>
                    <Button
                      color="success"
                      onClick={this.handleGoToOnboard}
                      style={{
                        padding: '10px 24px',
                        fontSize: 14,
                        fontWeight: 700,
                        borderRadius: 6,
                        boxShadow: '0 2px 4px rgba(22, 163, 74, 0.2)',
                      }}
                    >
                      <i className="fa fa-arrow-right" style={{ marginRight: 8 }} />
                      Proceed to Onboard Vehicle
                    </Button>
                  </div>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={this.closeAddVehicleModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {};
}

const ConnectedTripList = connect(mapStateToProps)(TripList);

// HOC to inject date filter, city context, and router history
const TripListWithContexts = (props) => {
  const history = useHistory();
  const { dateRange } = useDateFilter();
  const { selectedCities } = useCity();
  return <ConnectedTripList {...props} history={history} dateRange={dateRange} selectedCities={selectedCities} />;
};

export default TripListWithContexts;
